import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageSquare, Award, Sparkles, Heart, Lock, Plus, MessageCircle, Send, Trash2, Lightbulb } from "lucide-react";

function LockedGate({ addon = "Community Forum & Kudos" }) {
  return (
    <div className="hoa-card royal-ring p-10 text-center" data-testid="community-locked">
      <Lock className="w-8 h-8 text-gold mx-auto mb-3" />
      <h2 className="text-2xl font-semibold mb-2">{addon} is a premium add-on</h2>
      <p className="no-gold text-foreground/75 max-w-xl mx-auto mb-6">
        Trade ideas with other HOAs. Recognize owners doing good with photo posts and kudos. Boost morale together.
      </p>
      <a href="/subscription" className="inline-flex items-center gap-2 bg-gold text-secondary-foreground px-5 py-3 rounded-full font-semibold hover:brightness-110 glow-gold">
        <Sparkles className="w-4 h-4" /> Start free trial
      </a>
    </div>
  );
}

export default function CommunityHub() {
  const { user } = useAuth();
  const [locked, setLocked] = useState(false);
  const [posts, setPosts] = useState([]);
  const [kudos, setKudos] = useState([]);
  const [owners, setOwners] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [replyBody, setReplyBody] = useState("");

  const [postOpen, setPostOpen] = useState(false);
  const [postForm, setPostForm] = useState({ title: "", body: "", category: "General", tags: "" });

  const [kudoOpen, setKudoOpen] = useState(false);
  const [kudoForm, setKudoForm] = useState({ recipient_id: "", title: "", note: "", photo_url: "" });

  const loadPosts = async () => {
    try {
      const { data } = await api.get("/forum/posts");
      setPosts(data);
      setLocked(false);
    } catch (err) { if (err.response?.status === 402) setLocked(true); }
  };

  const loadKudos = async () => {
    try {
      const { data } = await api.get("/kudos");
      setKudos(data);
    } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("swallowed:", err?.message || err); }
  };

  const loadOwners = async () => {
    try {
      const { data } = await api.get("/users");
      setOwners(data);
    } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("swallowed:", err?.message || err); }
  };

  useEffect(() => { loadPosts(); loadKudos(); loadOwners(); }, []);

  const openPost = async (id) => {
    const { data } = await api.get(`/forum/posts/${id}`);
    setSelectedPost(data);
  };

  const createPost = async (e) => {
    e.preventDefault();
    try {
      const tags = postForm.tags.split(",").map((t) => t.trim()).filter(Boolean);
      await api.post("/forum/posts", { ...postForm, tags });
      toast.success("Posted");
      setPostOpen(false);
      setPostForm({ title: "", body: "", category: "General", tags: "" });
      loadPosts();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not post"); }
  };

  const sendReply = async () => {
    if (!replyBody.trim() || !selectedPost) return;
    try {
      await api.post(`/forum/posts/${selectedPost.id}/replies`, { body: replyBody });
      setReplyBody("");
      openPost(selectedPost.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not reply"); }
  };

  const createKudo = async (e) => {
    e.preventDefault();
    try {
      await api.post("/kudos", kudoForm);
      toast.success("Kudos posted");
      setKudoOpen(false);
      setKudoForm({ recipient_id: "", title: "", note: "", photo_url: "" });
      loadKudos();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not post"); }
  };

  const heartKudo = async (id) => {
    try {
      await api.post(`/kudos/${id}/heart`);
      loadKudos();
    } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("swallowed:", err?.message || err); }
  };

  const delKudo = async (id) => {
    if (!confirm("Delete this kudos?")) return;
    try { await api.delete(`/kudos/${id}`); loadKudos(); } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("swallowed:", err?.message || err); }
  };

  return (
    <div className="space-y-6" data-testid="community-hub-page">
      <div>
        <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
          <MessageSquare className="w-4 h-4" /> Community Hub
        </div>
        <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight">Ideas that worked. Neighbors doing good.</h1>
        <p className="no-gold text-foreground/75 mt-2 max-w-2xl">
          Share what's worked in your community, learn from what worked in others, and celebrate the owners who make
          this place feel like home.
        </p>
      </div>

      {locked ? <LockedGate /> : (
        <Tabs defaultValue="forum">
          <TabsList className="bg-card border border-border" data-testid="community-tabs">
            <TabsTrigger value="forum" data-testid="tab-forum">Forum</TabsTrigger>
            <TabsTrigger value="kudos" data-testid="tab-kudos">Kudos wall</TabsTrigger>
          </TabsList>

          <TabsContent value="forum" className="mt-6 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">Cross-community forum</h3>
              <Dialog open={postOpen} onOpenChange={setPostOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full h-11 bg-gold text-secondary-foreground hover:brightness-110" data-testid="new-post-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> New post
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Start a discussion</DialogTitle></DialogHeader>
                  <form onSubmit={createPost} className="space-y-4" data-testid="post-form">
                    <div>
                      <Label>Title</Label>
                      <Input required value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} className="mt-1.5 h-11" data-testid="post-title" />
                    </div>
                    <div>
                      <Label>Body</Label>
                      <Textarea required value={postForm.body} onChange={(e) => setPostForm({ ...postForm, body: e.target.value })} className="mt-1.5 min-h-[120px]" data-testid="post-body" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Category</Label>
                        <Input value={postForm.category} onChange={(e) => setPostForm({ ...postForm, category: e.target.value })} className="mt-1.5 h-11" data-testid="post-category" />
                      </div>
                      <div>
                        <Label>Tags (comma-separated)</Label>
                        <Input value={postForm.tags} onChange={(e) => setPostForm({ ...postForm, tags: e.target.value })} className="mt-1.5 h-11" placeholder="reserves, morale" data-testid="post-tags" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="post-submit">Post</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid lg:grid-cols-12 gap-4">
              <div className="lg:col-span-5 space-y-3">
                {posts.length === 0 && (
                  <div className="hoa-card p-6 text-center text-muted-foreground text-sm" data-testid="posts-empty">No posts yet. Be the first!</div>
                )}
                {posts.map((p) => (
                  <button key={p.id} onClick={() => openPost(p.id)} className={`w-full text-left hoa-card p-4 ${selectedPost?.id === p.id ? "border-gold royal-ring" : "hover:bg-accent/30"}`} data-testid={`post-${p.id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-gold-soft">{p.category}</div>
                    <div className="no-gold font-semibold">{p.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">by {p.author_name} · {new Date(p.created_at).toLocaleDateString()} · {p.reply_count} repl{p.reply_count === 1 ? "y" : "ies"}</div>
                  </button>
                ))}
              </div>
              <div className="lg:col-span-7">
                {!selectedPost ? (
                  <div className="hoa-card p-10 text-center text-muted-foreground" data-testid="no-post-selected">Open a post to read the discussion.</div>
                ) : (
                  <div className="hoa-card p-6 space-y-4" data-testid="post-detail">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gold-soft">{selectedPost.category}</div>
                      <h2 className="text-2xl font-semibold">{selectedPost.title}</h2>
                      <div className="text-xs text-muted-foreground mb-3">by {selectedPost.author_name} · {new Date(selectedPost.created_at).toLocaleString()}</div>
                      <p className="no-gold text-foreground/90 whitespace-pre-line leading-relaxed">{selectedPost.body}</p>
                    </div>
                    <div className="space-y-2 pt-3 border-t border-border">
                      {selectedPost.replies?.map((r) => (
                        <div key={r.id} className="p-3 rounded-lg bg-accent/30 border border-border" data-testid={`reply-${r.id}`}>
                          <div className="text-xs text-muted-foreground mb-1">{r.author_name} · {new Date(r.created_at).toLocaleString()}</div>
                          <div className="no-gold text-sm text-foreground/90 whitespace-pre-line">{r.body}</div>
                        </div>
                      ))}
                    </div>
                    <div className="pt-3 border-t border-border flex gap-2">
                      <Textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder="Share your take…" className="min-h-[60px] flex-1" data-testid="reply-input" />
                      <Button onClick={sendReply} className="rounded-full bg-gold text-secondary-foreground hover:brightness-110 self-end" data-testid="reply-send"><Send className="w-4 h-4" /></Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="kudos" className="mt-6 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">Catch a neighbor doing good.</h3>
              <Dialog open={kudoOpen} onOpenChange={setKudoOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full h-11 bg-gold text-secondary-foreground hover:brightness-110" data-testid="new-kudos-btn">
                    <Award className="w-4 h-4 mr-1.5" /> Give kudos
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Give kudos to a neighbor</DialogTitle></DialogHeader>
                  <form onSubmit={createKudo} className="space-y-4" data-testid="kudo-form">
                    <div>
                      <Label>Recipient</Label>
                      <Select value={kudoForm.recipient_id} onValueChange={(v) => setKudoForm({ ...kudoForm, recipient_id: v })}>
                        <SelectTrigger className="mt-1.5 h-11" data-testid="kudo-recipient"><SelectValue placeholder="Pick a neighbor" /></SelectTrigger>
                        <SelectContent>
                          {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.unit_number ? ` · ${o.unit_number}` : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input required value={kudoForm.title} onChange={(e) => setKudoForm({ ...kudoForm, title: e.target.value })} className="mt-1.5 h-11" placeholder="Cleaned up the playground!" data-testid="kudo-title" />
                    </div>
                    <div>
                      <Label>Note</Label>
                      <Textarea required value={kudoForm.note} onChange={(e) => setKudoForm({ ...kudoForm, note: e.target.value })} className="mt-1.5" data-testid="kudo-note" />
                    </div>
                    <div>
                      <Label>Photo URL (optional)</Label>
                      <Input value={kudoForm.photo_url} onChange={(e) => setKudoForm({ ...kudoForm, photo_url: e.target.value })} className="mt-1.5 h-11" placeholder="https://…" data-testid="kudo-photo" />
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="kudo-submit">Post kudos</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {kudos.length === 0 && (
                <div className="col-span-full hoa-card p-10 text-center text-muted-foreground" data-testid="kudos-empty">
                  No kudos yet — recognize someone!
                </div>
              )}
              {kudos.map((k) => (
                <div key={k.id} className="hoa-card p-5 flex flex-col" data-testid={`kudo-${k.id}`}>
                  {k.photo_url && (
                    <div className="rounded-xl overflow-hidden mb-3 aspect-video bg-accent/40">
                      <img src={k.photo_url} alt={k.title} className="w-full h-full object-cover" onError={(e) => (e.target.style.display = "none")} />
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-widest text-gold-soft">To {k.recipient_name}</div>
                  <div className="no-gold font-semibold text-base mt-0.5">{k.title}</div>
                  <p className="no-gold text-sm text-foreground/80 mt-1 leading-relaxed flex-1">{k.note}</p>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                    <div className="text-xs text-muted-foreground">from {k.from_name}</div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => heartKudo(k.id)} className="text-gold hover:brightness-110 flex items-center gap-1 text-sm font-mono" data-testid={`heart-${k.id}`}>
                        <Heart className="w-4 h-4" /> {k.hearts || 0}
                      </button>
                      {(k.from_id === user?.id || user?.role === "board" || user?.role === "admin") && (
                        <button onClick={() => delKudo(k.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-kudo-${k.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
